import type { PaletteMode, Components, Theme } from '@mui/material';
import { MuiButtonTheme } from './MuiButton.theme';
import { MuiButtonBaseTheme } from './MuiButtonBase.theme';
import { MuiCardTheme } from './MuiCard.theme';
import { MuiCheckboxTheme } from './MuiCheckbox.theme';
import { MuiCircularProgressTheme } from './MuiCircularProgress.theme';
import { MuiCollapseTheme } from './MuiCollapse.theme';
import { MuiDialogTheme } from './MuiDialog.theme';
import { MuiDividerTheme } from './MuiDivider.theme';
import { MuiPopoverTheme } from './MuiPopover.theme';
import { MuiIconButtonTheme } from './MuiIconButton.theme';
import { MuiTypographyTheme } from './MuiTypography.theme';
import { MuiChipTheme } from './MuiChip.theme';
import { MuiRadioTheme } from './MuiRadio.theme';
import { MuiSliderTheme } from './MuiSlider.theme';
import { MuiSvgIconTheme } from './MuiSvgIcon.theme';
import { MuiLinearProgressTheme } from './MuiLinearProgress.theme';
import { MuiMenuTheme } from './MuiMenu.theme';
import { MuiMenuItemTheme } from './MuiMenuItem.theme';
import { MuiAlertTheme } from './MuiAlert.theme';
import { MuiAutocompleteTheme } from './MuiAutocomplete.theme';
import { MuiDrawerTheme } from './MuiDrawer.theme';
import { MuiBreadcrumbsTheme } from './MuiBreadcrumbs.theme';
import { MuiSwitchTheme } from './MuiSwitch.theme';
import { MuiTabsTheme, MuiTabTheme } from './MuiTabs.theme';
import { MuiTextFieldTheme } from './MuiTextField.theme';
import {
  MuiPickersTextFieldTheme,
  MuiPickersDayTheme,
  MuiDateCalendarTheme,
  MuiDialogActionsTheme,
  MuiPickersLayoutTheme,
} from './MuiDatePicker.theme';
import { MuiDateRangePickerDayTheme, MuiDateRangeCalendarTheme } from './MuiDateRangePicker.theme';
import { MuiTooltipTheme } from './MuiTooltip.theme';
import { MuiToggleButtonTheme } from './MuiToggleButton.theme';
import { MuiToggleButtonGroupTheme } from './MuiToggleButtonGroup.theme';
import { MuiFormLabelTheme, MuiFormControlTheme } from './MuiForm.theme';
import { MuiSelectTheme } from './MuiSelect.theme';
import { MuiSkeletonTheme } from './MuiSkeleton.theme';
import { MuiListTheme, MuiListSubheaderTheme, MuiListItemTextTheme } from './MuiList.theme';
import {
  MuiTableTheme,
  MuiTableCellTheme,
  MuiTableRowTheme,
  MuiTableHeadTheme,
} from './MuiTable.theme';
import { MuiFormHelperTextTheme } from './MuiFormHelperText.theme';
import { MuiLinkTheme } from './MuiLink.theme';

export const themeComponents = (mode: PaletteMode): Components<Theme> => {
  const buttonTheme = MuiButtonTheme(mode);
  const buttonBaseTheme = MuiButtonBaseTheme(mode);
  const cardTheme = MuiCardTheme(mode);
  const checkboxTheme = MuiCheckboxTheme(mode);
  const typographyTheme = MuiTypographyTheme(mode);
  const chipTheme = MuiChipTheme(mode);
  const circularProgressTheme = MuiCircularProgressTheme();
  const collapseTheme = MuiCollapseTheme(mode);
  const dialogTheme = MuiDialogTheme(mode);
  const dividerTheme = MuiDividerTheme(mode);
  const popoverTheme = MuiPopoverTheme(mode);
  const iconButtonTheme = MuiIconButtonTheme(mode);
  const radioTheme = MuiRadioTheme(mode);
  const sliderTheme = MuiSliderTheme(mode);
  const svgIconTheme = MuiSvgIconTheme();
  const linearProgressTheme = MuiLinearProgressTheme(mode);
  const menuTheme = MuiMenuTheme(mode);
  const menuItemTheme = MuiMenuItemTheme(mode);
  const alertTheme = MuiAlertTheme(mode);
  const autocompleteTheme = MuiAutocompleteTheme(mode);
  const drawerTheme = MuiDrawerTheme();
  const breadcrumbsTheme = MuiBreadcrumbsTheme(mode);
  const switchTheme = MuiSwitchTheme(mode);
  const tabTheme = MuiTabTheme(mode);
  const tabsTheme = MuiTabsTheme(mode);
  const textFieldTheme = MuiTextFieldTheme(mode);
  const pickersTextFieldTheme = MuiPickersTextFieldTheme(mode);
  const pickersDayTheme = MuiPickersDayTheme(mode);
  const dateCalendarTheme = MuiDateCalendarTheme(mode);
  const pickersActionBarTheme = MuiDialogActionsTheme(mode);
  const pickersLayoutTheme = MuiPickersLayoutTheme(mode);
  const dateRangePickerDayTheme = MuiDateRangePickerDayTheme(mode);
  const dateRangeCalendarTheme = MuiDateRangeCalendarTheme(mode);
  const tooltipTheme = MuiTooltipTheme(mode);
  const toggleButtonTheme = MuiToggleButtonTheme(mode);
  const toggleButtonGroupTheme = MuiToggleButtonGroupTheme();
  const formLabelTheme = MuiFormLabelTheme(mode);
  const formControlTheme = MuiFormControlTheme();
  const selectTheme = MuiSelectTheme(mode);
  const skeletonTheme = MuiSkeletonTheme(mode);
  const listTheme = MuiListTheme();
  const listSubheaderTheme = MuiListSubheaderTheme(mode);
  const listItemTextTheme = MuiListItemTextTheme();
  const tableTheme = MuiTableTheme(mode);
  const tableCellTheme = MuiTableCellTheme(mode);
  const tableRowTheme = MuiTableRowTheme(mode);
  const tableHeadTheme = MuiTableHeadTheme(mode);
  const formHelperTextTheme = MuiFormHelperTextTheme(mode);
  const linkTheme = MuiLinkTheme(mode);

  return {
    MuiButton: buttonTheme,
    MuiButtonBase: buttonBaseTheme,
    MuiCard: cardTheme,
    MuiCheckbox: checkboxTheme,
    MuiCircularProgress: circularProgressTheme,
    MuiCollapse: collapseTheme,
    MuiDialog: dialogTheme,
    MuiDivider: dividerTheme,
    MuiFormHelperText: formHelperTextTheme,
    MuiPopover: popoverTheme,
    MuiIconButton: iconButtonTheme,
    MuiChip: chipTheme,
    MuiTypography: typographyTheme,
    MuiRadio: radioTheme,
    MuiSvgIcon: svgIconTheme,
    MuiLinearProgress: linearProgressTheme,
    MuiMenu: menuTheme,
    MuiMenuItem: menuItemTheme,
    MuiAlert: alertTheme,
    MuiAutocomplete: autocompleteTheme,
    MuiDrawer: drawerTheme,
    MuiBreadcrumbs: breadcrumbsTheme,
    MuiSlider: sliderTheme,
    MuiSwitch: switchTheme,
    MuiTab: tabTheme,
    MuiTabs: tabsTheme,
    MuiTextField: textFieldTheme,
    MuiPickersTextField: pickersTextFieldTheme,
    MuiPickersDay: pickersDayTheme,
    MuiDateCalendar: dateCalendarTheme,
    MuiDialogActions: pickersActionBarTheme,
    MuiPickersLayout: pickersLayoutTheme,
    MuiDateRangePickerDay: dateRangePickerDayTheme,
    MuiDateRangeCalendar: dateRangeCalendarTheme,
    MuiTooltip: tooltipTheme,
    MuiToggleButton: toggleButtonTheme,
    MuiToggleButtonGroup: toggleButtonGroupTheme,
    MuiFormControl: formControlTheme,
    MuiFormLabel: formLabelTheme,
    MuiSelect: selectTheme,
    MuiSkeleton: skeletonTheme,
    MuiList: listTheme,
    MuiListSubheader: listSubheaderTheme,
    MuiListItemText: listItemTextTheme,
    MuiTable: tableTheme,
    MuiTableCell: tableCellTheme,
    MuiTableRow: tableRowTheme,
    MuiTableHead: tableHeadTheme,
    MuiLink: linkTheme,
  };
};

export * from './MuiButton.theme';
export * from './MuiButtonBase.theme';
export * from './MuiCard.theme';
export * from './MuiCheckbox.theme';
export * from './MuiCircularProgress.theme';
export * from './MuiCollapse.theme';
export * from './MuiDialog.theme';
export * from './MuiDivider.theme';
export * from './MuiFormHelperText.theme';
export * from './MuiPopover.theme';
export * from './MuiIconButton.theme';
export * from './MuiChip.theme';
export * from './MuiCard.theme';
export * from './MuiCircularProgress.theme';
export * from './MuiRadio.theme';
export * from './MuiSlider.theme';
export * from './MuiSvgIcon.theme';
export * from './MuiLinearProgress.theme';
export * from './MuiMenu.theme';
export * from './MuiMenuItem.theme';
export * from './MuiAlert.theme';
export * from './MuiAutocomplete.theme';
export * from './MuiDrawer.theme';
export * from './MuiBreadcrumbs.theme';
export * from './MuiSwitch.theme';
export * from './MuiTabs.theme';
export * from './MuiTextField.theme';
export * from './MuiDatePicker.theme';
export * from './MuiDateRangePicker.theme';
export * from './MuiTooltip.theme';
export * from './MuiToggleButton.theme';
export * from './MuiToggleButtonGroup.theme';
export * from './MuiForm.theme';
export * from './MuiSelect.theme';
export * from './MuiSkeleton.theme';
export * from './MuiList.theme';
export * from './MuiTable.theme';
export * from './MuiLink.theme';
